// Modifier-aware clicks for controls that CANNOT be an anchor.
//
// Route-backed navigation renders as react-router `<Link>` wherever the markup
// allows it, which is what makes ctrl/⌘+click, middle-click, shift+click,
// "Open link in new tab" and "Copy link address" work — those are native
// behaviours of an `<a href>`, and the app had no anchors at all, so every one of
// them silently fell through to the JS handler and navigated in place.
//
// Two rows cannot become anchors, because their click already means something
// other than "navigate": the dashboard rail row BINDS a project's conversation on
// a single click (and opens on a double click), and a `/projects` row in select
// mode TOGGLES its checkbox. An anchor's default action fires on the first click
// and would break both. They keep their handler and consult `isNewTabClick`
// instead, so a modified click still reaches a new tab.

/**
 * Should this click open a new tab rather than navigate in place?
 *
 * Ctrl (Windows/Linux), ⌘ (macOS) and the middle button are the three gestures a
 * browser treats as "new tab" on a real link. Shift (new WINDOW) and alt (save)
 * are deliberately NOT here: `window.open` cannot honour either, so claiming them
 * would replace the browser's behaviour with a worse imitation. On a real `<Link>`
 * all of them keep working natively.
 */
export function isNewTabClick(e) {
  return !!e && (e.metaKey || e.ctrlKey || e.button === 1);
}

/**
 * Open an in-app path in a new tab.
 *
 * `noopener` because the new tab must not get a `window.opener` handle back into
 * this one. Mirrors the hand-rolled calls this helper replaces.
 */
export function openInNewTab(url) {
  window.open(url, '_blank', 'noopener');
}
